import { useState } from "react"
import { useParams } from "react-router-dom"
import { Tooltip } from "antd"
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import { Tag } from "components-design-system"
import DecisionTreeModal from "components/react-flow/DecisionTreeModal/DecisionTreeModal"
import dayjs from "dayjs"
import "./AssessmentsGrid.scss"

// cell Renderers
import AssessmentLinkCellRenderer from "./AssessmentLinkCellRenderer/AssessmentLinkCellRenderer"
import AssessmentResultCellRenderer from "components/ag-grid-cell-renderers/AssessmentResultCellRenderer/AssessmentResultCellRenderer"
import DueDateCellRenderer from "./DueDateCellRenderer/DueDateCellRenderer"

// embedded table cells
import ActivitiesCellRenderer from "./ActivitiesCellRenderer/ActivitiesCellRenderer"
import ActivityCompletionDatesCell from "components/assessments/linked-activities/ActivityCompletionDatesCell/ActivityCompletionDatesCell"

// api
import { useGetActivityTypes } from "api/hooks/activities/useActivitiesApi"
import { useGetAssessmentTypes } from "api/hooks/admin/useAssessmentTypesApi"
import { useGetStudyAssessment } from "api/hooks/studies/useAssessmentsApi"
import { useUserSettings } from "api/hooks"

//utils
import { DEFAULT_DATE_FORMAT } from "constants/globals"
import { getDateColDef, largeColDef } from "../../../../../../components/grids/columnTemplates"

const AssessmentsGrid = ({ gridRef, rowData, onFilterChanged, permissions, toggleLinkActivityModal, onGridReady }) => {
  const { studyId } = useParams()
  const { data: userSettings } = useUserSettings()
  const dateFormat = userSettings?.date_format || DEFAULT_DATE_FORMAT

  // activities
  const { data: activityTypes } = useGetActivityTypes()

  // decision tree modal
  const [treeModalOpen, setTreeModalOpen] = useState(false)
  // selected decision tree
  const { data: assessmentTypes } = useGetAssessmentTypes()
  const [selectedDt, setSelectedDt] = useState(null)
  // selected assessment metadata
  const [selectedAssessId, setSelectedAssessId] = useState()
  const { data: selectedAssessment } = useGetStudyAssessment(studyId, selectedAssessId)

  const getSelectedDt = (assessmentName) => {
    return assessmentTypes?.find((type) => type.name === assessmentName)?.dt
  }

  /**
   * Steps to open decision tree modal:
   * Get decision tree from assessment types (plural) api.
   * Get assessment's metadata from assessment (singular) api.
   */
  const openDecisionTree = async (assessItem) => {
    // load selected assessment for metadata
    setSelectedAssessId(assessItem.id)
    // set decision tree
    let dtData = getSelectedDt(assessItem.assessment_name)
    setSelectedDt(dtData)
    setTreeModalOpen(true)
  }

  const getRowHeight = ({ data }) => {
    if (data.activities) {
      const numRows = data.activities.length || 1
      return 35 * numRows
    }
  }

  const columnDefs = [
    {
      headerName: "Region",
      field: "region",
    },
    {
      headerName: "Assessment",
      field: "assessment_name",
      cellRenderer: AssessmentLinkCellRenderer,
      cellRendererParams: { studyId },
    },
    {
      headerName: "Tags",
      field: "tags",
      cellRenderer: ({ value }) => {
        return (
          <Tag.Group
            tags={value}
            maxTags={5}
          />
        )
      },
    },
    {
      headerName: "Type",
      field: "sub_type",
    },
    {
      field: "registry",
      cellRenderer: "RegistryCellRenderer",
      cellClass: ["col-badge"],
      minWidth: 150,
    },
    {
      headerName: "Result",
      field: "result",
      cellRenderer: AssessmentResultCellRenderer,
      cellRendererParams: {
        openDecisionTree,
      },
      filterParams: {
        valueFormatter: ({ value }) => {
          if (value === "Not Yet Assessed") return "Insufficient Data"
          return value
        },
      },
      cellClass: ["col-badge"],
    },
    {
      headerName: "Due",
      field: "assessment_due_date",
      cellRenderer: DueDateCellRenderer,
      type: "date",
      ...getDateColDef(),
    },
    {
      headerName: "Activities",
      field: "attached_projects",
      cellRenderer: ActivitiesCellRenderer,
      cellRendererParams: { activityTypes },
      minWidth: 150,
      valueGetter: ({ data }) => {
        if (data.attached_projects) return data.attached_projects.map((actObj) => actObj.title)
        return null
      },
      cellClass: ["table-cell"],
      filterParams: {
        caseSensitive: true,
      },
    },
    {
      headerName: "Activity Completion Date",
      colId: "activity_completion_date",
      minWidth: 150,
      cellRenderer: ({ data }) => (
        <ActivityCompletionDatesCell
          activities={data.attached_projects}
          completionDateKey="completion_date"
          completionDateTypeKey="completion_date_type"
        />
      ),
      valueGetter: ({ data }) => {
        if (data.attached_projects) return data.attached_projects.map((actObj) => actObj.completion_date)
        return null
      },

      filterParams: {
        valueFormatter: ({ value }) => {
          if (dayjs(value).isValid()) return dayjs(value).format(dateFormat)
          return value
        },
      },
      cellClass: ["table-cell"],
    },
    {
      headerName: "Delivery Due Date",
      field: "delivery_due_date",
      cellRenderer: DueDateCellRenderer,
      type: "date",
      ...getDateColDef(),
    },
    {
      headerName: "Local Submission Date",
      field: "local_submission_date",
      cellRenderer: DueDateCellRenderer,
      type: "date",
      ...getDateColDef(),
    },
    {
      headerName: "Record Publication Date",
      field: "record_publication_date",
      cellRenderer: DueDateCellRenderer,
      type: "date",
      ...getDateColDef(),
    },
    {
      headerName: "Comments",
      field: "comments",
      ...largeColDef,
    },
  ]

  // actions column

  if (permissions && permissions["project.create"] === true) {
    columnDefs.push({
      headerName: "",
      headerTooltip: "Create/Link Activities",
      colId: "actions",
      cellRenderer: ({ data }) => (
        <Tooltip title="Create/Link Activities">
          <i
            className="fal fa-plus-circle"
            onClick={() => toggleLinkActivityModal(data)}
          />
        </Tooltip>
      ),
      pinned: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      suppressColumnsToolPanel: true,
      cellClass: ["link-activities-cell"],
      maxWidth: 38,
    })
  }
  return (
    <div className="assessments-grid-container">
      <GridTypeA
        gridRef={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        onFilterChanged={onFilterChanged}
        getRowHeight={getRowHeight}
        domLayout="autoHeight"
        onGridReady={onGridReady}
        className="assessments-grid"
      />
      {selectedDt && selectedAssessment && (
        <DecisionTreeModal
          dt={{ schema: selectedAssessment?.res_schema }}
          assessment={selectedAssessment}
          showModal={treeModalOpen}
          onHide={() => {
            setTreeModalOpen(false)
            setSelectedDt(null)
          }}
        />
      )}
    </div>
  )
}

export default AssessmentsGrid
